import { useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { EmployeeDetails } from "./PayslipForm";

interface PayslipPreviewProps {
  details: EmployeeDetails;
  letterhead: string | null;
  signature: string | null;
  primaryColor: string;
  contentMargin: number;
  signaturePadding: number;
  onSave: () => void;
}

export const PayslipPreview = ({
  details,
  letterhead,
  signature,
  primaryColor,
  contentMargin,
  signaturePadding,
  onSave,
}: PayslipPreviewProps) => {
  const payslipRef = useRef<HTMLDivElement>(null);

  const calculateTotalEarnings = () => {
    return (
      (details.basicSalary || 0) +
      (details.hra || 0) +
      (details.conveyance || 0) +
      (details.medicalAllowance || 0) +
      (details.specialAllowance || 0)
    );
  };

  const calculateLeaveDeduction = () => {
    if (details.unpaidLeaves > 0) {
      const totalEarnings = calculateTotalEarnings();
      const perDaySalary = totalEarnings / 30;
      return perDaySalary * details.unpaidLeaves;
    }
    return 0;
  };

  const calculateTotalDeductions = () => {
    return (
      (details.providentFund || 0) +
      (details.professionalTax || 0) +
      (details.incomeTax || 0) +
      (details.otherDeductions || 0) +
      calculateLeaveDeduction()
    );
  };

  const calculateNetPay = () =>
    calculateTotalEarnings() - calculateTotalDeductions();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const downloadPDF = async () => {
    if (!payslipRef.current) return;
    onSave();

    const element = payslipRef.current;
    const canvas = await html2canvas(element, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: 793.7,
      height: 1122.5,
    });

    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF("p", "mm", "a4");
    pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
    pdf.save(`Payslip_${details.employeeId}_${details.payPeriod}.pdf`);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 mb-4">
        <button
          onClick={downloadPDF}
          className="btn btn-primary"
          style={{ backgroundColor: primaryColor }}
        >
          Download & Save PDF
        </button>
      </div>

      <div
        ref={payslipRef}
        className="payslip-container"
        style={{ "--primary-color": primaryColor } as React.CSSProperties}
      >
        {letterhead && (
          <img
            src={letterhead}
            alt="Letterhead"
            className="payslip-letterhead-bg"
          />
        )}

        <div
          className="payslip-body"
          style={{ paddingTop: `${contentMargin}mm` }}
        >
          <div
            className="payslip-header"
            style={{ borderBottomColor: primaryColor }}
          >
            <h1 className="payslip-title" style={{ color: primaryColor }}>
              Salary Slip
            </h1>
            <p style={{ fontSize: "14px", fontWeight: "bold" }}>
              Pay Period: {details.payPeriod}
            </p>
          </div>

          <div className="payslip-content">
            {/* Employee Information Section */}
            <div className="payslip-section">
              <h2
                className="payslip-section-title"
                style={{ borderBottomColor: primaryColor, color: primaryColor }}
              >
                Employee Information
              </h2>
              <div className="payslip-info-grid">
                <div className="payslip-info-item">
                  <span className="payslip-label">Employee Name:</span>{" "}
                  <span className="payslip-value">{details.employeeName}</span>
                </div>
                <div className="payslip-info-item">
                  <span className="payslip-label">Employee ID:</span>{" "}
                  <span className="payslip-value">{details.employeeId}</span>
                </div>
                <div className="payslip-info-item">
                  <span className="payslip-label">Designation:</span>{" "}
                  <span className="payslip-value">{details.designation}</span>
                </div>
                <div className="payslip-info-item">
                  <span className="payslip-label">Department:</span>{" "}
                  <span className="payslip-value">{details.department}</span>
                </div>
                <div className="payslip-info-item">
                  {/* <span className="payslip-label">Date of Joining:</span>{" "} */}
                  <span className="payslip-label">Pay Period:</span>{" "}
                  <span className="payslip-value">
                    {formatDate(details.dateOfJoining)}
                  </span>
                </div>
                <div className="payslip-info-item">
                  <span className="payslip-label">Leaves Taken:</span>
                  <span className="payslip-value">
                    {Number(details.paidLeaves) + Number(details.unpaidLeaves)}{" "}
                    ({details.paidLeaves} paid, {details.unpaidLeaves} unpaid)
                  </span>
                </div>
              </div>
            </div>

            {/* Salary Details Section */}
            <div className="payslip-section">
              <h2
                className="payslip-section-title"
                style={{ borderBottomColor: primaryColor, color: primaryColor }}
              >
                SALARY DETAILS
              </h2>
              <table className="payslip-table">
                <thead
                  style={{ backgroundColor: primaryColor, color: "white" }}
                >
                  <tr>
                    <th>EARNINGS</th>
                    <th style={{ textAlign: "right" }}>AMOUNT</th>
                    <th>DEDUCTIONS</th>
                    <th style={{ textAlign: "right" }}>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic Salary</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.basicSalary)}
                    </td>
                    <td>Provident Fund</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.providentFund)}
                    </td>
                  </tr>
                  <tr>
                    <td>HRA</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.hra)}
                    </td>
                    <td>Professional Tax</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.professionalTax)}
                    </td>
                  </tr>
                  <tr>
                    <td>Conveyance</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.conveyance)}
                    </td>
                    <td>Income Tax</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.incomeTax)}
                    </td>
                  </tr>
                  <tr>
                    <td>Medical Allowance</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.medicalAllowance)}
                    </td>
                    <td>Other Deductions</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.otherDeductions)}
                    </td>
                  </tr>
                  <tr>
                    <td>Special Allowance</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(details.specialAllowance)}
                    </td>
                    <td>
                      {calculateLeaveDeduction() > 0
                        ? "Unpaid Leave Deduction"
                        : ""}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {calculateLeaveDeduction() > 0
                        ? formatCurrency(calculateLeaveDeduction())
                        : ""}
                    </td>
                  </tr>
                  <tr className="payslip-total-row">
                    <td>Total Earnings</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(calculateTotalEarnings())}
                    </td>
                    <td>Total Deductions</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(calculateTotalDeductions())}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: primaryColor,
                  color: "white",
                  textAlign: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                  borderRadius: "5px",
                }}
              >
                Net Pay: {formatCurrency(calculateNetPay())}
              </div>
            </div>
          </div>

          <div style={{ flexGrow: 1 }}></div>

          {signature && (
            <div
              className="payslip-signature-wrapper"
              style={{
                marginBottom: `${signaturePadding}mm`,
                alignSelf: "flex-end",
              }}
            >
              <div className="payslip-footer-signature">
                <img src={signature} alt="Signature" />
                <div
                  className="signature-label"
                  style={{ borderTop: `1px solid ${primaryColor}` }}
                >
                  AUTHORIZED SIGNATORY
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
